import {
  Button,
  CommunityUsersByGroups,
  Loader,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from 'components';
import { COMMUNITY_ROUTES, PROFILE_ROUTES, ROUTES } from 'configs';
import { isEmpty } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { SkeletonListWrapper } from 'sidebars/AboutGroup/styled';
import {
  doCreateChatGroup,
  doUpdateChatGroup,
} from 'store/actionCreators/chat';
import { doGetCommunityMembersByGroups } from 'store/actionCreators/currentCommunity';
import { closeModal } from 'store/actionCreators/modal';
import { Chat, ChatUser, Conversation } from 'store/reducers/chat';
import { getMyId } from 'store/selectors/auth';
import {
  createChatGroupLoading,
  updateChatGroupLoading,
} from 'store/selectors/chat';
import {
  getCurrentCommunity,
  getCurrentCommunityLoading,
} from 'store/selectors/currentCommunity';
import { getData } from 'store/selectors/modal';
import { AddGroupMembersData } from 'types';
import { hasCommunityOrGroupAdminPermissions } from 'utils';

const AddGroupMembersChat = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const userId = useSelector(getMyId);
  const { communityId: paramsCommunityId } = useParams<{
    communityId: string;
  }>();
  const currentCommunityLoading = useSelector(getCurrentCommunityLoading);
  const createChat = useSelector(createChatGroupLoading);
  console.log('currentCommunityLoading', currentCommunityLoading);

  const data = useSelector(getData) as AddGroupMembersData & {
    chat?: Chat;
    members?: ChatUser[];
    isUpdate?: boolean;
    communityId: string;
    description?: string;
    chatName?: string;
    logo?: string;
  };
  const { chat, isUpdate, communityId, members, chatName, logo, description } =
    data;

  const community = useSelector(getCurrentCommunity);
  const updateGroupLoading = useSelector(updateChatGroupLoading);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isUpdate) {
      setSelectedUsers(userId ? [userId] : []);
    } else {
      setSelectedUsers(members.filter((m) => m.active).map((m) => m.userId));
    }
  }, [members, isUpdate]);

  useEffect(() => {
    dispatch(
      doGetCommunityMembersByGroups({
        communityId: community.id,
        withoutBlocked: true,
      }),
    );
  }, []);

  const filteredMembersByGroups = useMemo(() => {
    const source = paramsCommunityId
      ? community.membersByGroups
      : community.groups;
    return source
      ? source.map((group) => ({
          ...group,
          users: group.users || [],
        }))
      : [];
  }, [community.membersByGroups, community.groups, paramsCommunityId]);

  useEffect(() => {
    if (selectedUsers.length > 0 || filteredMembersByGroups.length > 0) {
      setIsLoading(false);
    }
  }, [selectedUsers, filteredMembersByGroups]);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const onSuccess = (data) => {
    if (!data?.admin?.includes(userId)) {
      if (paramsCommunityId) {
        navigate(`${ROUTES.COMMUNITY}/${communityId}/${COMMUNITY_ROUTES.CHAT}`);
      } else {
        navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.CHAT}`);
      }
    }
    handleClose();
    addToast(t('chat.update.success'), {
      appearance: 'success',
      autoDismiss: true,
    });
  };

  const onSuccessChat = (chatData: Conversation) => {
    dispatch(closeModal());

    addToast(t('chat.create.success'), {
      appearance: 'success',
      autoDismiss: true,
    });
    if (chatData?.admin?.includes(userId)) {
      if (paramsCommunityId) {
        navigate(
          `${ROUTES.COMMUNITY}/${communityId}/${COMMUNITY_ROUTES.CHAT}?talkId=${chatData.id}`,
        );
      } else {
        navigate(
          `${ROUTES.PROFILE}/${PROFILE_ROUTES.CHAT}?talkId=${chatData.id}`,
        );
      }
    }
  };

  const onSubmit = useCallback(() => {
    const admins = selectedUsers.filter(
      (element) =>
        hasCommunityOrGroupAdminPermissions(element, community) ||
        data.chat?.admin?.includes(element),
    );

    if (!isUpdate) {
      dispatch(
        doCreateChatGroup({
          chatName: chatName,
          description: description,
          logo: logo,
          userId,
          members: selectedUsers,
          onSuccess: onSuccessChat,
          communityId,
          admin: admins,
        }),
      );
    } else {
      dispatch(
        doUpdateChatGroup.trigger({
          userId,
          chatId: chat.id,
          communityId,
          payload: {
            chatName: chat.chatName,
            description: chat.description,
            logo: chat.logo,
            members: selectedUsers,
            admin: admins,
          },
          onSuccess,
        }),
      );
    }
  }, [selectedUsers, chat, communityId]);

  const filteredUsers = useMemo(() => {
    return (
      community?.users.filter(
        (u) => !filteredMembersByGroups.some((i) => i.id === u.id),
      ) || []
    );
  }, [community, filteredMembersByGroups]);

  const hasNoUsers = filteredUsers?.length <= 0;
  const hasNoFilteredMembers = filteredMembersByGroups.length <= 0;

  if (hasNoUsers && hasNoFilteredMembers) {
    return;
  }

  return (
    <Sidebar>
      <SidebarHeader
        label={
          isUpdate ? t('chat.modal.editMember') : t('group.addParticipants')
        }
        onCloseClick={handleClose}
      />
      <SidebarContent fullHeight>
        {currentCommunityLoading || isLoading ? (
          <SkeletonListWrapper>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="skeleton skeleton-list" />
            ))}
          </SkeletonListWrapper>
        ) : (
          <CommunityUsersByGroups
            value={selectedUsers}
            membersByGroups={filteredMembersByGroups}
            users={filteredUsers}
            height="calc(100svh - 269px)"
            onChange={setSelectedUsers}
            key={'memberSection'}
            forChat
            chatAdmin={data.chat.admin}
          />
        )}
      </SidebarContent>
      <SidebarFooter>
        <Button
          disabled={isEmpty(community.users) || updateGroupLoading}
          onClick={onSubmit}
        >
          {updateGroupLoading || createChat ? (
            <Loader type="button" size="28px" thickness={2} />
          ) : isUpdate ? (
            t('chat.update.btn')
          ) : (
            t('chat.update.btn')
          )}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AddGroupMembersChat;
